
import { RevLink } from '@backmarket/design-system'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import AdminMenu from '@navigation/components/AdminMenu.vue'
import BurgerMenuDesktop from '@navigation/components/BurgerMenuDesktop.vue'
import BurgerMenuMobile from '@navigation/components/BurgerMenuMobile.vue'
import CartIconAndBubble from '@navigation/components/CartIconAndBubble.vue'
import Logo from '@navigation/components/Logo'
import UserIconAndSubMenu from '@navigation/components/UserIconAndSubMenu.vue'
import {
  HEADER_ANIMATION_THRESHOLD,
  HEADER_TRACKING_NAME,
  HEADER_TRACKING_ZONE,
} from '@navigation/constant'
import { ROUTES } from '@router'
import SearchBar from '@search/SearchBar'
import { trackClick } from '@tracking/events'

import translations from './Header.translations'

export default {
  components: {
    AdminMenu,
    SearchBar,
    StoreNav: () => import('@navigation/components/StoreNav'),
    RevLink,
    BurgerMenuDesktop,
    BurgerMenuMobile,
    Logo,
    UserIconAndSubMenu,
    CartIconAndBubble,
  },

  data() {
    return {
      headerVisible: true,
      lastScrollPosition: 0,
      scrollDistanceDown: 0,
    }
  },

  computed: {
    translations: () => translations,
    ROUTES: () => ROUTES,
    HEADER_TRACKING_NAME: () => HEADER_TRACKING_NAME,
    HEADER_TRACKING_ZONE: () => HEADER_TRACKING_ZONE,
    ...mapGetters({
      isNavigationOpened: 'isNavigationOpened',
      topSales: 'countryConfig/sales',
      isBuyBackEnabled: 'config/isBuyBackEnabled',
      isStaff: 'user/isStaff',
      isShadowingClient: 'user/isShadowingClient',
      isShadowingMerchant: 'user/isShadowingMerchant',
      country: 'config/country',
      experiments: 'flags/getExperiments',
      isEnabled: 'precipitation/isEnabled',
    }),

    buybackLink() {
      return {
        name: ROUTES.CMS.BUYBACK,
        params: {
          pageName: 'home',
        },
      }
    },

    isAdmin() {
      return this.isStaff || this.isShadowingClient || this.isShadowingMerchant
    },

    // We are gonna keep this legacy logic until the end of the ab-test.
    // We can't easily get rid of the style in the header's admin
    // And it's way easier for the Q/A to keep using data-test during the ab-test (E2E on old header work with data-test)
    // https://backmarket.atlassian.net/browse/GNL-2089
    consolidatedTopSales() {
      return this.topSales
        .filter((sale) => sale.title && !isEmpty(sale.link))
        .map((sale) => ({
          ...sale,
          style: { color: sale.style.text_color },
          test: `topsales-${sale.title}`,
        }))
    },

    altTitle() {
      if (this.isEnabled) {
        return this.$t(this.translations.snowAltDisable)
      }

      return this.$t(this.translations.snowAltEnable)
    },
  },

  mounted() {
    if (!isEmpty(this.$refs.headerContainer)) {
      this.$refs.headerContainer.style.height = `${this.$refs.header.clientHeight}px`
    }
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.resizeHeader)
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.resizeHeader)
  },

  methods: {
    // We re-use the old method to open the sub navigation
    // It is not a good practice but this was not part of the MVP
    // You can read the spec here
    // https://backmarket.atlassian.net/wiki/spaces/NS/pages/2093088813/P+J+Revamp+header
    openSubMenu() {
      const action = this.isNavigationOpened
        ? 'closeNavigation'
        : 'openNavigation'

      this.$store.dispatch(action)
      this.trackBurgerMenuClick()
    },

    trackBurgerMenuClick() {
      trackClick({
        zone: this.HEADER_TRACKING_NAME.NAVIGATION,
        name: 'all_product',
      })
    },

    trackHorizontalNav(sale, index) {
      this.trackClick(`topsales-${index}-${sale.title}`)
    },

    trackClick(name) {
      trackClick({
        zone: this.HEADER_TRACKING_ZONE,
        name,
      })
    },
    resizeHeader() {
      if (!isEmpty(this.$refs.headerContainer)) {
        this.$refs.headerContainer.style.height = `${this.$refs.header.clientHeight}px`
      }
    },
    handleScroll() {
      window.requestAnimationFrame(() => {
        const { header } = this.$refs

        const currentScrollPosition = window.pageYOffset
        const isScrollingUp =
          this.previousScrollPosition > currentScrollPosition
        const isScrollingDown =
          this.previousScrollPosition < currentScrollPosition

        if (
          isScrollingDown &&
          window.pageYOffset > HEADER_ANIMATION_THRESHOLD
        ) {
          header.classList.add(this.$style.hidden)
        }

        if (isScrollingUp) {
          header.classList.remove(this.$style.hidden)
        }

        this.previousScrollPosition = currentScrollPosition
      })
    },

    togglePrecipitations() {
      this.$store.dispatch('precipitation/toggle')
    },
  },
}
